import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  Menu,
  Select,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { ArrowLeft, Send, UserPlus } from "lucide-react";
import { api, tenantApi } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth";
import { contactApi, type ContactConversation, type ContactMessage } from "@/lib/api/contact";
import { RoutedToBadges } from "@/components/support/routed-to-badges";
import { UserHoverCard } from "@nodus/ui";
import { usePortalEvent, RECONNECT_EVENT } from "@/lib/portal-socket";

// On a ticket the member picks each message's recipient: "Agent" (the human
// thread via postMessage, no AI reply) or "Assistant" (the AI replies via
// supportChat). The AI must NOT auto-reply on a ticket — it only replies when
// the member explicitly picks "Assistant".
type Recipient = "agent" | "assistant";

// ─── Message bubble ───────────────────────────────────────────────────────────

function MessageBubble({
  msg,
  currentUserId,
  participants,
}: {
  msg: ContactMessage;
  currentUserId?: number;
  participants: ContactConversation["participants"];
}) {
  // Only the signed-in member's OWN messages render as the right-aligned "you"
  // bubble. A colleague is also role "user" on a multi-participant ticket, so we
  // key off the author id — their message renders left-aligned with their name,
  // exactly like staff/AI, just without a badge. Member-typed messages are plain
  // text (never markdown); only AI + staff render markdown.
  const isMine = msg.role === "user" && msg.author_user_id != null && msg.author_user_id === currentUserId;
  const isMemberAuthored = msg.role === "user";
  const isStaff = !msg.is_ai && msg.role === "staff";

  // When colleagues share the ticket, surface who each message is from via a
  // hover card with their role. Resolve the author's participant role; staff /
  // AI aren't participants, so label them by their message role.
  const showAuthorInfo = participants.length > 1;
  const authorParticipant = msg.author_user_id != null
    ? participants.find((p) => p.id === msg.author_user_id)
    : undefined;
  const authorRole = authorParticipant
    ? authorParticipant.role === "owner"
      ? "Owner"
      : "Colleague"
    : isStaff
      ? "Staff"
      : undefined;
  const authorName = msg.author_name || "Anonymous";

  return (
    <Flex direction="column" align={isMine ? "flex-end" : "flex-start"} gap={4}>
      {!isMine && (
        <Group gap={6} px={4} wrap="nowrap">
          {/* Attribution rule: is_ai → "AI"; staff → author + "Staff" tag;
              else author_name (a colleague or yourself elsewhere); else "Anonymous".
              On a shared ticket the name gets a hover card with the author's role. */}
          {msg.is_ai || !showAuthorInfo ? (
            <Text fz={11} c="dimmed">
              {msg.is_ai ? "AI" : authorName}
            </Text>
          ) : (
            <UserHoverCard name={authorName} role={authorRole} size={11} />
          )}
          {msg.is_ai && (
            <Badge size="xs" variant="light" color="gray">
              AI
            </Badge>
          )}
          {isStaff && (
            <Badge size="xs" variant="light" color="green">
              Staff
            </Badge>
          )}
        </Group>
      )}
      <Box
        px={16}
        py={10}
        fz="sm"
        maw="75%"
        bg={
          isMine
            ? // A dark brand shade (not the lighter "filled") so white text clears
              // WCAG 2.1 AA / EN 301 549 (EAA) contrast for any customer brand
              // colour — the filled shade (brand-6/7) is too light for white text.
              "var(--mantine-color-brand-8)"
            : isStaff
              ? "var(--mantine-color-green-light)"
              : "var(--mantine-color-default-hover)"
        }
        c={isMine ? "var(--mantine-color-white)" : "var(--mantine-color-text)"}
        style={{ borderRadius: 16 }}
      >
        {isMemberAuthored ? (
          // Member messages (yours or a colleague's): plain escaped text, never markdown
          <Text fz="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {msg.content}
          </Text>
        ) : (
          // AI + human-author messages: rendered as markdown
          <Box fz="sm" style={{ lineHeight: 1.6, overflow: "hidden" }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                img: ({ ...props }) => (
                  <img {...props} style={{ maxWidth: "100%", height: "auto", borderRadius: 4 }} />
                ),
              }}
            >
              {msg.content}
            </ReactMarkdown>
          </Box>
        )}
      </Box>
    </Flex>
  );
}

// ─── Participants bar ───────────────────────────────────────────────────────────

function ParticipantsBar({
  conversation,
  onAdded,
}: {
  conversation: ContactConversation;
  onAdded: () => void;
}) {
  const [adding, setAdding] = useState(false);

  // The detail payload exposes the authoritative participant list (owner-first),
  // including colleagues who have been added but haven't posted yet.
  const participants = conversation.participants;

  // Tenant users available to add (any participant may add another tenant user).
  const { data: accounts } = useQuery({
    queryKey: ["tenant-accounts"],
    queryFn: () => tenantApi.getAccounts(1, 200).then((r) => r.data.results),
    staleTime: 5 * 60 * 1000,
  });

  function displayName(a: { first_name: string; last_name: string; email: string }) {
    const full = `${a.first_name} ${a.last_name}`.trim();
    return full || a.email;
  }

  async function addParticipant(userId: number) {
    if (adding) return;
    setAdding(true);
    try {
      await contactApi.addParticipant(conversation.id, userId);
      onAdded();
    } finally {
      setAdding(false);
    }
  }

  return (
    <Stack
      gap={6}
      px="md"
      py="xs"
      style={{ borderBottom: "1px solid var(--mantine-color-default-border)", flexShrink: 0 }}
    >
      {conversation.group_ids.length > 0 && (
        <RoutedToBadges groupIds={conversation.group_ids} />
      )}
      <Group gap="xs" wrap="wrap">
      <Text fz="xs" c="dimmed">
        Participants:
      </Text>
      {participants.length <= 1 ? (
        <Text fz="xs" c="dimmed">Just you</Text>
      ) : (
        participants.map((p) => (
          <Text key={p.id} fz="xs">
            {p.name}
            {p.role === "owner" ? " (owner)" : ""}
          </Text>
        ))
      )}
      <Box flex={1} />
      <Menu shadow="md" width={240} position="bottom-end">
        <Menu.Target>
          <Button
            variant="subtle"
            size="compact-xs"
            loading={adding}
            leftSection={<UserPlus style={{ height: 14, width: 14 }} />}
          >
            Add
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Add a colleague</Menu.Label>
          {!accounts || accounts.length === 0 ? (
            <Menu.Item disabled>No colleagues found</Menu.Item>
          ) : (
            accounts.map((a) => (
              <Menu.Item key={a.id} onClick={() => void addParticipant(a.id)}>
                {displayName(a)}
              </Menu.Item>
            ))
          )}
        </Menu.Dropdown>
      </Menu>
      </Group>
    </Stack>
  );
}

// ─── Thread view ──────────────────────────────────────────────────────────────

function ContactThread({ convId }: { convId: number }) {
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [recipient, setRecipient] = useState<Recipient>("agent");
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentUserId = useAuthStore((s) => s.user?.id);
  const queryClient = useQueryClient();
  const queryKey = ["contact-conversation", convId];

  const { data: conversation, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      api.get<ContactConversation>(`/support/conversations/${convId}/`).then((r) => r.data),
  });

  // Realtime support_message now carries the full conversation in its payload
  // (same shape as GET /support/conversations/:id/). Apply it straight into the
  // cache to skip the slow GET; fall back to a refetch only when it's absent.
  usePortalEvent("support_message", (e) => {
    if (e.conversation_id && e.conversation_id !== convId) return;
    const pushed = e.conversation as ContactConversation | undefined;
    if (pushed) {
      queryClient.setQueryData(queryKey, pushed);
    } else {
      void refetch();
    }
  });
  // A reconnect should still reconcile against the server.
  usePortalEvent(RECONNECT_EVENT, () => void refetch());

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation?.history, isSending]);

  // Default the recipient toggle to whoever replied last — if the AI answered
  // last, keep talking to the Assistant; if a human did, talk to the Agent.
  // Runs once per conversation (the component is keyed by convId, so it remounts
  // on switch); never overrides a manual toggle the member makes afterwards.
  const recipientInitialized = useRef(false);
  useEffect(() => {
    if (recipientInitialized.current || !conversation) return;
    recipientInitialized.current = true;
    const lastResponder = [...(conversation.history ?? [])]
      .reverse()
      .find((m) => m.role === "assistant" || m.role === "staff");
    if (lastResponder) {
      setRecipient(lastResponder.role === "assistant" ? "assistant" : "agent");
    }
  }, [conversation]);

  async function sendMessage() {
    const text = input.trim();
    if (!text || isSending) return;
    setInput("");
    setIsSending(true);
    try {
      // Recipient toggle: "Agent" posts to the human thread (no AI reply);
      // "Assistant" runs the AI. The AI only replies when explicitly asked.
      if (recipient === "assistant") {
        // supportChat returns SupportChatResponse (history only, not the full
        // conversation shape) — keep the refetch so participants/ticket state stay
        // correct. The realtime push will also apply the full payload shortly.
        await tenantApi.supportChat(text, convId);
        await refetch();
      } else {
        // postMessage returns the full ConversationDetailResponse — apply it
        // directly to avoid the slow follow-up GET.
        const { data } = await contactApi.postMessage(convId, text);
        queryClient.setQueryData(queryKey, data);
      }
    } finally {
      setIsSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  const messages: ContactMessage[] = conversation?.history ?? [];

  return (
    <Flex direction="column" h="calc(100vh - 56px - 48px)">
      {conversation && <ParticipantsBar conversation={conversation} onAdded={() => void refetch()} />}

      {/* Messages */}
      <Box style={{ flex: 1, overflowY: "auto" }} p="md">
        <Stack gap="md">
          {isLoading && (
            <Flex justify="center" py="xl">
              <Loader size={20} />
            </Flex>
          )}
          {!isLoading && messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              msg={msg}
              currentUserId={currentUserId}
              participants={conversation?.participants ?? []}
            />
          ))}
          {/* While the Assistant is composing a reply (can take a while), show a
              left-aligned "thinking" bubble so it's clear something's happening. */}
          {isSending && recipient === "assistant" && (
            <Flex direction="column" align="flex-start" gap={4}>
              <Group gap={6} px={4}>
                <Text fz={11} c="dimmed">
                  AI
                </Text>
                <Badge size="xs" variant="light" color="gray">
                  AI
                </Badge>
              </Group>
              <Box px={16} py={10} bg="var(--mantine-color-default-hover)" style={{ borderRadius: 16 }}>
                <Group gap={8} wrap="nowrap">
                  <Loader size={14} />
                  <Text fz="sm" c="dimmed">
                    Assistant is thinking…
                  </Text>
                </Group>
              </Box>
            </Flex>
          )}
          <div ref={bottomRef} />
        </Stack>
      </Box>

      {/* Reply input */}
      <Group
        gap="xs"
        p="md"
        style={{ borderTop: "1px solid var(--mantine-color-default-border)", flexShrink: 0 }}
      >
        <SegmentedControl
          size="xs"
          value={recipient}
          onChange={(v) => setRecipient(v as Recipient)}
          disabled={isSending}
          data={[
            { label: "Agent", value: "agent" },
            { label: "Assistant", value: "assistant" },
          ]}
        />
        <TextInput
          ref={inputRef}
          placeholder="Type a reply..."
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void sendMessage();
            }
          }}
          disabled={isSending}
          flex={1}
        />
        <Button size="sm" onClick={() => void sendMessage()} disabled={isSending || !input.trim()} p={8}>
          {isSending ? <Loader size={16} /> : <Send style={{ height: 16, width: 16 }} />}
        </Button>
      </Group>
    </Flex>
  );
}

// ─── New contact form ─────────────────────────────────────────────────────────

function NewContactForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Preselect a group when deep-linked from the Support page (?group=ID), and
  // prefill the subject/message when deep-linked from another page that wants
  // to start a specific request (e.g. the Network page's "Contact support"
  // when no network/WiFi is assigned: ?subject=…&message=…).
  const [groupId, setGroupId] = useState<string | null>(searchParams.get("group"));
  const [category, setCategory] = useState(searchParams.get("subject") ?? "");
  const [message, setMessage] = useState(searchParams.get("message") ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: groups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ["contact-groups"],
    queryFn: () => contactApi.getContactGroups().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const selectData = groups.map((g) => ({ value: String(g.id), label: g.name }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!groupId || !message.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await contactApi.createRequest({
        group_ids: [Number(groupId)],
        category: category.trim() || undefined,
        message: message.trim(),
      });
      navigate(`/contact/${res.data.id}`, { replace: true });
    } catch {
      setError("Failed to send your message. Please try again.");
      setIsSubmitting(false);
    }
  }

  return (
    // w="100%" is required: this Box is a flex item (the page is a column
    // Flex), and `mx="auto"` alone makes a flex item collapse to content width
    // instead of stretching. With w + maw it fills the column up to maw, centered.
    <Box w="100%" maw={760} mx="auto" px="lg" py="xl">
      <Title order={2} mb="lg">Contact</Title>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="lg">
          <Select
            label="Send to"
            placeholder={groupsLoading ? "Loading…" : "Select a group"}
            data={selectData}
            value={groupId}
            onChange={setGroupId}
            disabled={groupsLoading}
            searchable
            required
            size="md"
          />
          <TextInput
            label="Subject (optional)"
            placeholder="e.g. Maintenance request"
            value={category}
            onChange={(e) => setCategory(e.currentTarget.value)}
            size="md"
          />
          <Textarea
            label="Message"
            placeholder="Describe your request…"
            value={message}
            onChange={(e) => setMessage(e.currentTarget.value)}
            minRows={6}
            autosize
            required
            size="md"
          />
          {error && (
            <Text fz="sm" c="red">
              {error}
            </Text>
          )}
          <Button
            type="submit"
            size="md"
            loading={isSubmitting}
            disabled={!groupId || !message.trim()}
          >
            Send
          </Button>
        </Stack>
      </form>
    </Box>
  );
}

// ─── Page root ────────────────────────────────────────────────────────────────

export default function ContactPage() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const convId = conversationId ? parseInt(conversationId, 10) : undefined;

  return (
    <Flex direction="column" h="100%">
      {/* Header */}
      <Group
        gap="xs"
        px="md"
        py="sm"
        style={{ borderBottom: "1px solid var(--mantine-color-default-border)", flexShrink: 0 }}
      >
        <Button variant="subtle" size="compact-md" onClick={() => navigate(-1)} aria-label="Back" p={4}>
          <ArrowLeft style={{ height: 16, width: 16 }} />
        </Button>
        <Text fw={600}>{convId ? "Conversation" : "Contact"}</Text>
      </Group>

      {/* key={convId} remounts per conversation so the recipient toggle, draft,
          and in-flight state never leak across conversation switches. */}
      {convId ? <ContactThread key={convId} convId={convId} /> : <NewContactForm />}
    </Flex>
  );
}
